
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface FishingType {
    id_fishingtype: string;
    fishingtypes_name: string;
    isactive?: boolean;
}
type FishingTypeInput = Omit<FishingType, 'id_fishingtype'>

interface FishingTypesProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface FishingTypesContextData {
    createFishingType: (fishingType: FishingTypeInput) => Promise<ResponseProps>;
    updateFishingType: (fishingType: FishingType) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
}

interface UseFishingTypesOptions {
    loadFishingTypes?: boolean;
}

interface UseFishingTypesData extends FishingTypesContextData {
    fishingTypes: FishingType[];
}

const FishingTypesContext = createContext<FishingTypesContextData>(
    {} as FishingTypesContextData
);

const fishingTypesQueryKey = ['fish-types'];
const emptyFishingTypes: FishingType[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchFishingTypes() {
  const response = await apiV2.get('fish-types');
  return Array.isArray(response?.data) ? response.data : [];
}

export function FishingTypesProvider({ children }: FishingTypesProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateFishingTypes() {
    queryClient.invalidateQueries({ queryKey: fishingTypesQueryKey });
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/fish-types/${id}`);
    if (response.status === 200) {
      invalidateFishingTypes();
      return {
        success: true,
        message: "Espécie removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover espécie."
    };
  }

  async function createFishingType(fishingTypesInput: FishingTypeInput) {
    const response = await apiV2.post('/fish-types', fishingTypesInput);
    if (response.status === 201) {
      invalidateFishingTypes();
      return {
        success: true,
        message: "Espécie adicionada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar espécie."
    };

  }

  async function updateFishingType(fishingType: FishingType) {
    const response = await apiV2.put(`/fish-types/${fishingType.id_fishingtype}`, fishingType);
    if (response.status === 204) {
      invalidateFishingTypes();
      return {
        success: true,
        message: "Espécie editada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar espécie."
    };
  }

  return (
    <FishingTypesContext.Provider value={{ createFishingType, updateFishingType, updateOnDelete }}>
      {children}
    </FishingTypesContext.Provider>
  );
}

export function UseFishingTypes(options: UseFishingTypesOptions = {}): UseFishingTypesData {
  const context = useContext(FishingTypesContext);
  const { loadFishingTypes = false } = options;

  const fishingTypesQuery = useQuery({
    queryKey: fishingTypesQueryKey,
    queryFn: fetchFishingTypes,
    enabled: loadFishingTypes && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    fishingTypes: fishingTypesQuery.data || emptyFishingTypes
  };
}
